import { inject, injectable } from "inversify";
import { DependencyType } from "../../di/DependencyType";
import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import {
  CreateBatchDocument,
  CreateBatchInput,
  CreateBatchMutation,
  CreateBatchMutationVariables,
  BatchesDocument,
  BatchesQuery,
  BatchesQueryVariables,
  UpdateBatchDocument,
  UpdateBatchInput,
  UpdateBatchMutation,
  UpdateBatchMutationVariables,
} from "generated/graphql";

type BatchNode = NonNullable<
  NonNullable<BatchesQuery["batches"]>["nodes"]
>[number];

@injectable()
export class BatchService {
  constructor(
    @inject(DependencyType.ApolloClient)
    private readonly apolloClient: ApolloClient<NormalizedCacheObject>
  ) {}

  async getAllBatches(refresh = false) {
    console.log("refresh", refresh);
    const fetchPolicy = refresh ? "network-only" : ("cache-first" as const);
    const all: BatchNode[] = [];
    let after: string | undefined;

    while (true) {
      const { data, errors, error } = await this.apolloClient.query<
        BatchesQuery,
        BatchesQueryVariables
      >({
        query: BatchesDocument,
        variables: { after },
        fetchPolicy,
      });
      if (error || errors || !data) {
        console.error(error, errors);
        throw new Error("Error fetching batches");
      }
      const connection = data.batches;
      all.push(...(connection?.nodes ?? []));
      if (
        !connection?.pageInfo?.hasNextPage ||
        !connection.pageInfo.endCursor
      ) {
        break;
      }
      after = connection.pageInfo.endCursor;
    }

    return [...all].sort((a, b) => a.batchNumber.localeCompare(b.batchNumber));
  }

  async createBatch(input: CreateBatchInput) {
    const { data, errors } = await this.apolloClient.mutate<
      CreateBatchMutation,
      CreateBatchMutationVariables
    >({
      mutation: CreateBatchDocument,
      variables: {
        input,
      },
    });
    if (errors || !data) {
      console.error(errors);
      throw new Error("Error creating batch");
    }
    return data.createBatch;
  }

  async updateBatch(input: UpdateBatchInput) {
    const { data, errors } = await this.apolloClient.mutate<
      UpdateBatchMutation,
      UpdateBatchMutationVariables
    >({
      mutation: UpdateBatchDocument,
      variables: {
        input,
      },
    });
    if (errors || !data) {
      console.error(errors);
      throw new Error("Error updating batch");
    }
    return data.updateBatch;
  }
}
